import { Close } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useCreateTaskSetMutation, useTaskSetQuery, useUpdateTaskSetMutation } from "@/hooks/tasks";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";

import FormErrorAlert from "../FormErrorAlert";
import { NewSetValues } from "../SetCalendar/CalendarContext";
import Sidebar, { SidebarHeader } from "../Sidebar";
import { getClimbTypeForSetType } from "../TaskForm/getSetTypeChoices";
import {
  CreateTaskSetFormValues,
  EditTaskSetFormValues,
  taskSetToFormValues,
} from "../TaskSetForm/formValues";
import { FormButtons, TaskSetForm } from "../TaskSetForm/TaskSetForm";

const CloseButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <IconButton
      sx={{
        backgroundColor: "#01002455",
        border: "1px solid",
        borderColor: "#343F6555",
        borderRadius: "100%",
        "&:hover": {
          backgroundColor: "neutral.800",
        },
      }}
      onClick={onClick}
    >
      <Close sx={{ color: "white" }} />
    </IconButton>
  );
};

export default function TaskSetDetailSidebar({
  uuid,
  createNew,
  newSetValues,
  onClose,
}: {
  uuid?: string;
  createNew?: boolean;
  newSetValues?: NewSetValues;
  onClose: () => void;
}) {
  const venueUuid = useCurrentVenueUuid();
  const { data: venue } = useCurrentVenueQuery();

  const { data: taskSet } = useTaskSetQuery(uuid, venueUuid);

  const createMutation = useCreateTaskSetMutation();
  const editMutation = useUpdateTaskSetMutation();
  const navigate = useNavigate();

  const defaultValues: CreateTaskSetFormValues | EditTaskSetFormValues | undefined = useMemo(() => {
    if (createNew) {
      const hasBoulder = venue?.climbTypes.includes("BOULDER");
      const type = newSetValues?.type || (hasBoulder ? "circuit" : "sport");
      const climbType = getClimbTypeForSetType(type);
      return {
        startDate: newSetValues?.date || format(new Date(), "yyyy-MM-dd"),
        endDate: newSetValues?.date || format(new Date(), "yyyy-MM-dd"),
        climbType: climbType !== undefined ? climbType : hasBoulder ? "BOULDER" : "SPORT",
        zone: null,
        circuit: null,
        notes: null,
        minGradeValue: null,
        maxGradeValue: null,
        gradeSystem: venue?.climbTypes.includes("BOULDER")
          ? venue.boulderGradeSystem
          : venue?.sportGradeSystem,
        stripExisting: true,
        type,
        competition: newSetValues?.competition || null,
      } as CreateTaskSetFormValues;
    } else if (taskSet) {
      return taskSetToFormValues(taskSet);
    }
  }, [
    taskSet,
    venue?.climbTypes,
    venue?.boulderGradeSystem,
    venue?.sportGradeSystem,
    createNew,
    newSetValues,
  ]);

  const methods = useForm<CreateTaskSetFormValues | EditTaskSetFormValues>({
    defaultValues,
  });

  useEffect(() => {
    if (!newSetValues) {
      return;
    }
    if (newSetValues.date) {
      methods.setValue("startDate", newSetValues.date);
      methods.setValue("endDate", newSetValues.date);
    }
    if (newSetValues.type) {
      methods.setValue("type", newSetValues.type);
      const climbType = getClimbTypeForSetType(newSetValues.type);
      if (climbType !== undefined) {
        methods.setValue("climbType", climbType);
      }
    }
  }, [methods.setValue, newSetValues]);

  const onCreateSubmit = (data: CreateTaskSetFormValues) => {
    if (!venue) {
      return;
    }
    const payload = {
      ...data,
      venue: venue.uuid,
      notes: data.notes ?? undefined,
      type: undefined,
      ...(data.climbType === null
        ? {
            minGradeValue: undefined,
            maxGradeValue: undefined,
            gradeSystem: undefined,
            stripExisting: undefined,
            routesCount: undefined,
          }
        : undefined),
    };
    return createMutation.mutateAsync(payload).then((data) => {
      navigate({
        to: "/gym/$venueUuid/schedule/set/$setUuid",
        params: {
          venueUuid: venue.uuid,
          setUuid: data.uuid,
        },
      });
    });
  };

  const onEditSubmit = (data: EditTaskSetFormValues) => {
    const payload = {
      ...data,
      notes: data.notes ?? undefined,
    };
    if (!taskSet) {
      return;
    }
    return editMutation.mutateAsync({ uuid: taskSet.uuid, payload }).then(() => {
      onClose();
    });
  };

  // The default values follow createNew, so the form holds that mode's values.
  const onSubmit = (data: CreateTaskSetFormValues | EditTaskSetFormValues) =>
    createNew
      ? onCreateSubmit(data as CreateTaskSetFormValues)
      : onEditSubmit(data as EditTaskSetFormValues);

  useEffect(() => {
    taskSet && methods.reset(taskSetToFormValues(taskSet));
  }, [taskSet, methods]);

  return (
    <Sidebar
      sx={{
        borderTopLeftRadius: 16,
        height: "100%",
        borderLeft: "1px solid",
        borderLeftColor: "neutral.200",
      }}
    >
      <SidebarHeader>
        <Typography level="title-lg">Create a set</Typography>
        <CloseButton onClick={onClose} />
      </SidebarHeader>
      <FormProvider {...methods}>
        <Stack
          sx={{ flex: 1 }}
          component="form"
          autoComplete="off"
          onSubmit={methods.handleSubmit(onSubmit)}
          onReset={() => methods.reset()}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
              flexBasis: 0,
            }}
          >
            <FormErrorAlert />
            <TaskSetForm taskSet={taskSet} createNew={createNew} />
          </Box>
          <Box sx={{ flexShrink: 0, minHeight: 40 }}>
            <FormButtons taskSet={taskSet} createNew={createNew} />
          </Box>
        </Stack>
      </FormProvider>
    </Sidebar>
  );
}
